import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/**
 * Export a schedule to an Excel file with colored cells
 * @param {Object[]} participants
 * @param {Object[]} schedule
 * @param {number[]} holidays
 * @param {number} days
 * @param {Function} t - translation function
 */
export const exportToExcel = ({
  participants,
  schedule,
  holidays,
  days,
  t,
}) => {
  const headers = [
    t("NAME"),
    t("TOTAL"),
    t("HOLIDAY"),
    ...Array.from({ length: days }, (_, i) => `${i + 1}`),
  ];
  const data = [headers];

  participants.forEach((person, pIndex) => {
    const totalShifts = Object.values(schedule[pIndex]).filter(
      (val) => val !== "" && val !== "-",
    ).length;
    const holidayShifts = Object.entries(schedule[pIndex]).filter(
      ([dayIndex, val]) =>
        val !== "" && val !== "-" && holidays.includes(Number(dayIndex)),
    ).length;
    const row = [
      person.name,
      totalShifts,
      holidayShifts,
      ...Array.from(
        { length: days },
        (_, dayIndex) => schedule[pIndex][dayIndex] || "",
      ),
    ];
    data.push(row);
  });

  const totalShiftsAll = participants.reduce((acc, _, pIndex) => {
    return (
      acc +
      Object.values(schedule[pIndex]).filter((val) => val !== "" && val !== "-")
        .length
    );
  }, 0);

  const totalHolidayShiftsAll = participants.reduce((acc, _, pIndex) => {
    return (
      acc +
      Object.entries(schedule[pIndex]).filter(
        ([dayIndex, val]) =>
          val !== "" && val !== "-" && holidays.includes(Number(dayIndex)),
      ).length
    );
  }, 0);

  const totalRow = [
    t("TOTAL"),
    totalShiftsAll,
    totalHolidayShiftsAll,
    ...Array.from({ length: days }, (_, dayIndex) =>
      participants.reduce((acc, _, pIndex) => {
        return (
          acc +
          (schedule[pIndex][dayIndex] !== "" &&
          schedule[pIndex][dayIndex] !== "-"
            ? 1
            : 0)
        );
      }, 0),
    ),
  ];
  data.push(totalRow);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Schedule");

  data.forEach((row) => worksheet.addRow(row));

  worksheet.columns.forEach((column, colIndex) => {
    if (colIndex >= 3) {
      let maxLength = 4;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = maxLength + 2;
    }
  });

  data.forEach((row, rowIndex) => {
    row.forEach((cellValue, colIndex) => {
      const cell = worksheet.getRow(rowIndex + 1).getCell(colIndex + 1);
      let fillColor = null;

      if (rowIndex === 0) {
        cell.font = { bold: true };
        if (colIndex >= 3 && holidays.includes(colIndex - 3)) {
          fillColor = "FFFFFF99";
        }
      } else if (colIndex >= 3) {
        const dayIndex = colIndex - 3;
        if (holidays.includes(dayIndex)) fillColor = "FFFFFF99";
        if (cellValue !== "" && cellValue !== "-") fillColor = "FFADD8E6";
        else if (cellValue === "-") fillColor = "FFF08080";
      }

      if (fillColor) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };
      }
    });
  });
  // ---------- Sheet 2: day-by-day assignments ----------
  const isShift = (v) => v !== "" && v !== "-" && v != null;

  // names per day, in participant order
  const namesPerDay = Array.from({ length: days }, (_, dayIndex) =>
    participants
      .filter((_, pIndex) => isShift(schedule[pIndex][dayIndex]))
      .map((p) => p.name),
  );

  const maxShifts = Math.max(1, ...namesPerDay.map((n) => n.length));

  const byDay = workbook.addWorksheet("By Day");

  byDay.addRow([
    t("DAYS"),
    ...Array.from({ length: maxShifts }, (_, i) => `${t("SHIFT")}${i + 1}`),
  ]);

  namesPerDay.forEach((names, dayIndex) => {
    byDay.addRow([
      `${t("DAY")} ${dayIndex + 1}`,
      ...Array.from({ length: maxShifts }, (_, i) => names[i] || ""),
    ]);
  });

  // header style
  byDay.getRow(1).font = { bold: true };

  // holiday rows colored (all columns, even empty ones)
  namesPerDay.forEach((_, dayIndex) => {
    if (!holidays.includes(dayIndex)) return;
    const row = byDay.getRow(dayIndex + 2); // +1 header, +1 one-based
    for (let c = 1; c <= maxShifts + 1; c++) {
      row.getCell(c).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF99" },
      };
    }
  });

  // column widths
  byDay.columns.forEach((column) => {
    let maxLength = 8;
    column.eachCell({ includeEmpty: true }, (cell) => {
      maxLength = Math.max(
        maxLength,
        cell.value ? cell.value.toString().length : 0,
      );
    });
    column.width = maxLength + 2;
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "schedule_colored.xlsx",
    );
  });
};
